import { useState } from "react";

import { repriceScore } from "../api/client";
import type { ConfidenceLabel, OfferRange, TiltMap } from "../types/api";
import { formatPrice, parsePriceInput } from "../utils/price";
import { TiltMapChart } from "./TiltMapChart";

/**
 * ProScorePanel — the hero of the Pro summary tile. Leads with the property's
 * Tilt Map position + quadrant label + buyer recommendation (the single 0-100
 * Tilt Score was retired 2026-06-29). Editing the asking price RE-PRICES the
 * score server-side (zero external calls) so the dot, quadrant, and offer range
 * all move together — the thresholds never reach the browser.
 */

const QUADRANT_CLASS: Record<string, string> = {
  Buy: "q-buy",
  Negotiate: "q-negotiate",
  Consider: "q-consider",
  Evaluate: "q-evaluate",
  "Insufficient data": "q-insufficient",
};

interface Props {
  scoreId: string;
  // Server-computed offer range (methodology §2.2 — FVE-anchored, keyed off the
  // quadrant); updated live on reprice. Thresholds never reach the client.
  offerRange: OfferRange;
  tiltMap: TiltMap | null;
  confidence: ConfidenceLabel;
  initialAskingPrice: number;
  address?: string | null;
}

export function ProScorePanel({
  scoreId,
  offerRange: initialOfferRange,
  tiltMap: initialTiltMap,
  confidence,
  initialAskingPrice,
  address,
}: Props) {
  const [priceText, setPriceText] = useState(formatPrice(initialAskingPrice));
  const [price, setPrice] = useState<number>(initialAskingPrice);
  const [tiltMap, setTiltMap] = useState<TiltMap | null>(initialTiltMap);
  // Offer range comes from the server and moves with the dot on reprice.
  const [offerRange, setOfferRange] = useState<OfferRange>(initialOfferRange);
  const [repricing, setRepricing] = useState(false);

  async function commitPrice() {
    const parsed = parsePriceInput(priceText);
    if (!parsed || parsed <= 0) {
      setPriceText(formatPrice(price)); // revert to last good value
      return;
    }
    setPriceText(formatPrice(parsed));
    if (parsed === price) return; // no change → no reprice
    setPrice(parsed);

    if (!scoreId) return; // can't reprice without an id (legacy path)
    setRepricing(true);
    try {
      // Server recompute at the new price — moves the dot + quadrant + range
      // together, no external calls, thresholds stay server-side.
      const repriced = await repriceScore(scoreId, parsed);
      setTiltMap(repriced.tilt_map);
      setOfferRange(repriced.offer_range);
    } catch {
      // 404 (score predates facts archival) or network — keep the last dot +
      // range; the price field still shows the value the user typed.
    } finally {
      setRepricing(false);
    }
  }

  const placeable = tiltMap && tiltMap.quadrant !== "Insufficient data";
  // §2.2 semantics: no FVE / INSUFFICIENT → range unavailable; a 0% range with
  // equal bounds means the ask is at/below fair value → "offer at/near ask".
  const rangeUnavailable = !placeable;
  const atOrNearAsk =
    placeable && offerRange.low_pct === 0 && offerRange.high_pct === 0;

  const offerSection = (
    <div className="pro-score-offer">
      <h3>Your offer range</h3>
      {rangeUnavailable ? (
        <p className="pro-offer-range">Offer range unavailable — verify with a CMA.</p>
      ) : atOrNearAsk ? (
        <>
          <p className="pro-offer-range">At or near asking</p>
          <p className="pro-offer-pct">
            The ask is at or below fair value — secure it; little price room.
          </p>
        </>
      ) : (
        <>
          <p className="pro-offer-range">
            {formatPrice(offerRange.low_price)} – {formatPrice(offerRange.high_price)}
          </p>
          <p className="pro-offer-pct">
            {offerRange.low_pct.toFixed(1)}%–{offerRange.high_pct.toFixed(1)}% off asking
          </p>
        </>
      )}

      <label className="pro-price-edit">
        Asking price
        <input
          type="text"
          inputMode="decimal"
          value={priceText}
          onChange={(e) => setPriceText(e.target.value)}
          onBlur={commitPrice}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          aria-label="Asking price (edit to recompute the Tilt Map + offer range)"
        />
      </label>

      <p className="pro-score-confidence">Confidence: {confidence}</p>
    </div>
  );

  return (
    <div className="pro-score-panel">
      {placeable ? (
        <>
          <div className="pro-quadrant-head">
            <span
              className={`quadrant-badge ${QUADRANT_CLASS[tiltMap!.quadrant] ?? ""}`}
            >
              {tiltMap!.quadrant}
            </span>
            <span className="pro-quadrant-caption">
              {repricing ? "updating…" : "Tilt Map"}
            </span>
          </div>
          {tiltMap!.recommendation && (
            <p className="pro-quadrant-rec">{tiltMap!.recommendation}</p>
          )}
          {tiltMap!.short_sale_lender_gated && (
            <p className="pro-quadrant-flag">
              ⚠ Short-sale / lender-gated: the seller has little equity, so any
              discount needs lender approval and the timeline is slower.
            </p>
          )}
          <div className="pro-score-body">
            <div
              className={
                repricing ? "pro-quadrant-map pro-quadrant-busy" : "pro-quadrant-map"
              }
            >
              <TiltMapChart
                points={[
                  {
                    id: "this",
                    label: address ?? "This property",
                    tiltMap,
                    highlight: true,
                  },
                ]}
              />
            </div>
            {offerSection}
          </div>
        </>
      ) : (
        <>
          <p className="pro-quadrant-rec">
            Not enough licensed data to place this property on the Tilt Map.
          </p>
          <div className="pro-score-body">{offerSection}</div>
        </>
      )}
    </div>
  );
}
